// Tenant slug validation pattern: lowercase letters, numbers, hyphens (1-63 chars)
// Examples: 'acme', 'my-org', 'company123'
// Invalid: 'My-Org' (uppercase), '-start' (starts with hyphen), 'end-' (ends with hyphen)
const TENANT_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Reserved user portal routes that should not be treated as tenants.
// These segments prevent misinterpretation as tenant slugs in path-based routing.
// Users navigate to /{tenant}/dashboard, not /dashboard directly.
// Keep in sync with ROUTES constant in auth-routes.ts
const RESERVED_ROUTES = new Set([
  'dashboard',
  'profile',
  'account-settings',
  'login',
  'tenant-selection',
  'onboarding',
  'tenant-onboarding',
  'sandbox',
]);

/**
 * Validate if a string is a valid tenant slug
 * Excludes reserved user portal routes
 */
export const isValidTenantSlug = (slug: string | undefined): slug is string => {
  if (!slug) {
    return false;
  }

  if (RESERVED_ROUTES.has(slug.toLowerCase())) {
    return false;
  }

  return slug.length >= 1 && slug.length <= 63 && TENANT_PATTERN.test(slug);
};

/**
 * Extract tenant from a specific path string
 * Useful for testing or processing paths other than the current location
 */
export const getTenantFromPathString = (pathString: string): string | null => {
  const segments = pathString.split('/').filter(Boolean);

  if (segments.length > 0) {
    const potentialTenant = segments[0];

    return isValidTenantSlug(potentialTenant) ? potentialTenant : null;
  }

  return null;
};

/**
 * Extract tenant from current URL path
 * Returns the first path segment if it's a valid tenant slug
 */
export const getTenantFromPath = (): string | null => {
  return getTenantFromPathString(window.location.pathname);
};

/**
 * Build a tenant-prefixed path (throws on invalid input)
 * @param tenant - The tenant slug (must be non-empty)
 * @param path - The path to prefix (with or without leading slash)
 * @returns Tenant-prefixed path like '/{tenant}/path'
 * @throws {Error} If tenant is empty or invalid
 */
export const buildTenantPath = (tenant: string, path: string): string => {
  if (!tenant || typeof tenant !== 'string') {
    throw new Error('Tenant must be a non-empty string');
  }

  if (!isValidTenantSlug(tenant)) {
    throw new Error(
      `Invalid tenant slug: "${tenant}". Must match pattern ${TENANT_PATTERN}`
    );
  }

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `/${tenant}${normalizedPath}`;
};

/** Base domain: strips the leading subdomain (e.g. 'app.spark.dev' → 'spark.dev'). */
export const getBaseDomain = (): string => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return 'spark.dev';
  }

  const dotIndex = hostname.indexOf('.');

  if (dotIndex === -1) {
    return hostname;
  }

  const remainder = hostname.substring(dotIndex + 1);

  // No dot in remainder means hostname was already a bare domain (e.g., 'spark.dev')
  return remainder.includes('.') ? remainder : hostname;
};

/**
 * Build a full origin URL for a given subdomain
 * e.g., buildSubdomainOrigin('admin') → 'https://admin.spark.dev:5173'
 *
 * Uses current window protocol and port for consistency.
 */
export const buildSubdomainOrigin = (subdomain: string): string => {
  const { protocol, port } = window.location;
  const baseDomain = getBaseDomain();
  const portSuffix = port ? `:${port}` : '';

  return `${protocol}//${subdomain}.${baseDomain}${portSuffix}`;
};

/**
 * Origin of the auth (IdP) service for the current tenant, which runs on the
 * tenant subdomain ({slug}.{domain}). Null when no tenant is in the path.
 */
export const authServiceOrigin = (): string | null => {
  const tenant = getTenantFromPath();

  return tenant ? buildSubdomainOrigin(tenant) : null;
};
